import React, { useRef, useState, useCallback } from 'react';
import './MessageCompose.css';

const MAX_LENGTH = 4000;

/**
 * Rich message compose box.
 *
 * Props:
 *   onSend({ body: string, files: File[] }): Promise<void>  — called with trimmed body + selected files; throws on error
 *   placeholder: string
 *   members: [{_id, name}] | null        — when provided, enables @mentions
 *   disabled: boolean
 */
export default function MessageCompose({
  onSend,
  placeholder = 'Write a message...',
  members = null,
  disabled = false,
}) {
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  // @mention state
  const [mentionQuery, setMentionQuery] = useState(null); // null = not active
  const [mentionStart, setMentionStart] = useState(0);    // char index of '@' in body
  const [mentionActive, setMentionActive] = useState(0);  // keyboard-selected row

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── Auto-grow textarea ───────────────────────────────────────────────────────
  const autoGrow = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  }, []);

  // ── Filtered members for dropdown ────────────────────────────────────────────
  const filteredMembers =
    members && mentionQuery !== null
      ? members
          .filter((m) => m.name.toLowerCase().includes(mentionQuery.toLowerCase()))
          .slice(0, 8)
      : [];

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const val = e.target.value;
    if (val.length > MAX_LENGTH) return; // hard cap
    setBody(val);
    setError('');
    autoGrow();

    // Detect @word pattern before cursor
    if (members) {
      const cursor = e.target.selectionStart;
      const beforeCursor = val.slice(0, cursor);
      const match = beforeCursor.match(/@(\w*)$/);
      if (match) {
        setMentionQuery(match[1]);
        setMentionStart(cursor - match[0].length);
        setMentionActive(0);
      } else {
        setMentionQuery(null);
      }
    }
  };

  const insertMention = useCallback(
    (member) => {
      const cursor = textareaRef.current?.selectionStart ?? body.length;
      const before = body.slice(0, mentionStart);
      const after = body.slice(cursor);
      const insertion = `@${member.name} `;
      const newBody = before + insertion + after;
      if (newBody.length <= MAX_LENGTH) {
        setBody(newBody);
        setMentionQuery(null);
        setTimeout(() => {
          const pos = before.length + insertion.length;
          textareaRef.current?.setSelectionRange(pos, pos);
          textareaRef.current?.focus();
          autoGrow();
        }, 0);
      }
    },
    [body, mentionStart, autoGrow]
  );

  const canSend = (body.trim().length > 0 || files.length > 0) && body.length <= MAX_LENGTH && !sending && !disabled;

  const handleFileChange = (e) => {
    const nextFiles = Array.from(e.target.files || []);
    if (nextFiles.length === 0) return;
    setFiles((prev) => [...prev, ...nextFiles]);
    e.target.value = '';
    setError('');
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const doSend = useCallback(async () => {
    if (!canSend) return;
    setSending(true);
    setError('');
    try {
      await onSend({ body: body.trim(), files });
      setBody('');
      setFiles([]);
      setMentionQuery(null);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to send.');
    } finally {
      setSending(false);
    }
  }, [canSend, onSend, body, files]);

  const handleKeyDown = (e) => {
    // Escape closes dropdown
    if (e.key === 'Escape' && mentionQuery !== null) {
      setMentionQuery(null);
      return;
    }

    // Arrow / Enter / Tab navigation inside dropdown
    if (mentionQuery !== null && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionActive((v) => Math.min(v + 1, filteredMembers.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionActive((v) => Math.max(v - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredMembers[mentionActive]);
        return;
      }
    }

    // Ctrl+Enter / Cmd+Enter to send
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      doSend();
    }
  };

  const remaining = MAX_LENGTH - body.length;
  const showCounter = body.length > MAX_LENGTH * 0.75;

  return (
    <div className={`msg-compose${disabled ? ' disabled' : ''}`}>
      {/* @mention dropdown — rendered above compose box */}
      {mentionQuery !== null && filteredMembers.length > 0 && (
        <div className="mention-dropdown">
          {filteredMembers.map((m, i) => (
            <button
              key={m._id}
              type="button"
              className={`mention-item${i === mentionActive ? ' active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault(); // keep textarea focused
                insertMention(m);
              }}
            >
              <span className="mention-avatar">{m.name[0].toUpperCase()}</span>
              <span className="mention-name">{m.name}</span>
            </button>
          ))}
        </div>
      )}

      {error && <p className="compose-error">{error}</p>}

      {files.length > 0 && (
        <div className="compose-files">
          {files.map((file, index) => (
            <div key={`${file.name}-${index}`} className="compose-file-chip">
              <span className="compose-file-name">{file.name}</span>
              <button
                type="button"
                className="compose-file-remove"
                onClick={() => removeFile(index)}
                aria-label={`Remove ${file.name}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="compose-inner">
        <button
          type="button"
          className="compose-attach-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || sending}
          title="Add files"
        >
          +
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="compose-file-input"
          onChange={handleFileChange}
          disabled={disabled || sending}
        />
        <textarea
          ref={textareaRef}
          className="compose-textarea"
          placeholder={placeholder}
          value={body}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled || sending}
          rows={1}
        />
        <div className="compose-side">
          {showCounter && (
            <span
              className={`compose-counter${remaining < 200 ? ' warn' : ''}${remaining < 0 ? ' over' : ''}`}
            >
              {remaining}
            </span>
          )}
          <button
            type="button"
            className={`compose-send-btn${canSend ? ' ready' : ''}`}
            onClick={doSend}
            disabled={!canSend}
            title="Send (Ctrl+Enter)"
          >
            {/* Paper-plane icon */}
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </div>
      </div>

      <p className="compose-hint">
        Ctrl+Enter to send · + to attach files{members ? ' · @ to mention' : ''}
      </p>
    </div>
  );
}
